/**
 * @file: useMediaCaptureWithAnnotation.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { CameraEvent } from '@euler/app/components/media/camera/CameraView.shared';
import { ImageAnnotationResult } from '@euler/app/flows/media/annotation/components/ImageAnnotationView';
import { AnnotationTagOption } from '@euler/app/flows/media/annotation/functions/AnnotationTagOption';
import { DefaultAnnotationTag } from '@euler/app/flows/media/annotation/functions/DefaultAnnotationTag';
import { ImageAnnotationInput } from '@euler/app/flows/media/annotation/types';
import { ContinuousMediaCaptureResult } from '@euler/app/flows/media/types';
import { useAppLoading } from '@euler/components/loading/Provider';
import { getImageMask } from '@euler/functions/getImageMask';
import { isMobileBrowser } from '@euler/functions/responsiveness';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { MediaService } from '@euler/lib/media/MediaService';
import { ImageAnnotationScene } from '@euler/model/enum';
import { delayed, mimeTypeFromFileUri } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { SaveFormat, manipulateAsync } from 'expo-image-manipulator';
import { ReactNode } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { Observable } from 'rxjs';
import invariant from 'tiny-invariant';

export const useMediaCaptureWithAnnotation = ({
  scene,
  multiple = true,
  maxMediaCountToSelect = 100,
  mediaCaptureRequired,
  photoOnly,
}: {
  scene: ImageAnnotationScene;
  multiple?: boolean;
  maxMediaCountToSelect?: number;
  mediaCaptureRequired?: boolean;
  photoOnly?: boolean;
}) => {
  const navigation = useAppNavigation();
  const appLoading = useAppLoading();
  const dimens = useWindowDimensions();
  const storeConfiguration = useStoreConfiguration();
  const isAnnotationDisabled = Boolean(
    storeConfiguration.app?.imageAnnotationDisabledForScenes?.includes(scene),
  );

  return usePersistFn(
    ({
      caption,
      watermark,
      tags,
      tagOptions,
      defaultTag,
      uploadVideoFile,
      handleImageAnnotationResults,
      continuous,
      ...options
    }: {
      maxMediaCountToSelect?: number;
      mediaCaptureRequired?: boolean;
      event$?: Observable<CameraEvent>;
      caption?: string | (() => ReactNode);
      tags?: () => string[];
      tagOptions?: () => AnnotationTagOption[] | undefined;
      defaultTag?: () => DefaultAnnotationTag | undefined;
      watermark?: (scale?: number) => ReactNode;
      continuous?: boolean;
      uploadVideoFile?: (
        localFileUri: string,
        contentType: string,
        rotate: number | undefined,
      ) => Promise<void>;
      handleImageAnnotationResults: (
        results: ImageAnnotationResult[],
      ) => Promise<void>;
    }) => {
      navigation.navigate('_camera', {
        multiple,
        maxMediaCountToSelect:
          options.maxMediaCountToSelect ?? maxMediaCountToSelect,
        mediaCaptureRequired:
          options.mediaCaptureRequired ?? mediaCaptureRequired,
        event$: options.event$,
        photoOnly,
        caption,
        continuous,
        watermark,
        onContinuousFinished: continuous
          ? (results: ContinuousMediaCaptureResult[]) => {
              navigation.pop();
              const photos: ImageAnnotationResult[] = [];
              for (const item of results) {
                if (item.type === 'video') {
                  void uploadVideoFile?.(
                    item.uri,
                    item.contentType,
                    item.rotate,
                  );
                } else {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { type: _type, ...photo } = item;
                  photos.push(photo);
                }
              }
              if (photos.length > 0) {
                void handleImageAnnotationResults(photos);
              }
            }
          : undefined,
        onMultipleAssetsSelected: async assets => {
          if (Platform.OS === 'web' && !isMobileBrowser()) {
            navigation.pop();
          } else {
            navigation.pop(2);
          }

          const videos = assets.filter(x => x.mediaType === 'video');
          const photos = assets.filter(x => x.mediaType === 'photo');

          for (const video of videos) {
            invariant(
              uploadVideoFile != null,
              'uploadVideoFile callback is required',
            );
            if (Platform.OS === 'web') {
              void uploadVideoFile(
                video.uri,
                mimeTypeFromFileUri(video.filename || video.uri, 'video/mp4'),
                undefined,
              );
            } else {
              const assetInfo =
                await MediaService.shared.getAssetInfoAsync(video);
              if (assetInfo.localUri) {
                void uploadVideoFile(
                  assetInfo.localUri,
                  mimeTypeFromFileUri(
                    assetInfo.filename || assetInfo.localUri,
                    'video/mp4',
                  ),
                  undefined,
                );
              }
            }
          }

          if (!photos.length) {
            return;
          }

          const inputs: ImageAnnotationInput[] = [];
          await appLoading.showWithAction(async () => {
            for (const asset of photos) {
              if (Platform.OS === 'web') {
                inputs.push({
                  id: asset.id,
                  uri: asset.uri,
                  width: asset.width,
                  height: asset.height,
                });
              } else {
                const assetInfo =
                  await MediaService.shared.getAssetInfoAsync(asset);
                inputs.push({
                  id: assetInfo.id,
                  uri: assetInfo.localUri ?? assetInfo.uri,
                  width: assetInfo.width,
                  height: assetInfo.height,
                  orientation: assetInfo.orientation,
                });
              }
            }
          });

          if (isAnnotationDisabled) {
            const results: ImageAnnotationResult[] = [];
            for (const input of inputs) {
              let imageUri = input.uri;
              if (Platform.OS !== 'web') {
                const res = await manipulateAsync(
                  input.uri,
                  input.width > 1336
                    ? [
                        {
                          resize: {
                            //Fix: web result has no size
                            width: 1336,
                            height: (input.height / input.width) * 1336,
                          },
                        },
                      ]
                    : [],
                  {
                    compress: 0.8,
                    format: SaveFormat.JPEG,
                  },
                );
                imageUri = res.uri;
              }

              results.push({
                imageUri,
                originalImageUri: undefined,
                annotationMetadata: undefined,
                width: input.width,
                height: input.height,
              });
            }
            void handleImageAnnotationResults(results);
            return;
          }

          if (inputs.length === 1) {
            const maskFromInput = (input: ImageAnnotationInput) => {
              return getImageMask({
                previewWidth: dimens.width,
                previewHeight: dimens.height,
                imageWidth: input.width,
                imageHeight: input.height,
              });
            };
            navigation.navigate('_imageAnnotation', {
              imageUri: inputs[0].uri,
              mask: maskFromInput(inputs[0]),
              tags: tags?.() ?? [],
              tagOptions: tagOptions?.(),
              defaultTag: defaultTag?.(),
              watermark,
              onDone: res => {
                void handleImageAnnotationResults([res]);
                navigation.pop();
              },
            });
          } else {
            navigation.navigate('_imageAnnotationMulti', {
              inputs: inputs,
              tags: tags?.() ?? [],
              tagOptions: tagOptions?.(),
              defaultTag: defaultTag?.(),
              watermark,
              onDone: res => {
                navigation.goBack();
                delayed(() => {
                  void handleImageAnnotationResults(res);
                }, 100);
              },
            });
          }
        },
        onResult: result => {
          if (result.type === 'photo') {
            if (isAnnotationDisabled) {
              void handleImageAnnotationResults([
                {
                  imageUri: result.uri,
                  originalImageUri: undefined,
                  annotationMetadata: undefined,
                  width: result.mask.width,
                  height: result.mask.height,
                },
              ]);
              if (Platform.OS !== 'web' || isMobileBrowser()) {
                navigation.pop();
              }
            } else {
              navigation.navigate('_imageAnnotation', {
                imageUri: result.uri,
                mask: result.mask,
                tags: tags?.() ?? [],
                tagOptions: tagOptions?.(),
                defaultTag: defaultTag?.(),
                watermark,
                onDone: res => {
                  void handleImageAnnotationResults([res]);
                  navigation.pop(2);
                },
              });
            }
          } else {
            invariant(
              uploadVideoFile != null,
              'uploadVideoFile callback is required',
            );
            void uploadVideoFile(result.uri, result.contentType, result.rotate);
            navigation.pop();
          }
        },
      });
    },
  );
};
